import { useSelector } from "react-redux";
import {
  useHotelSlot,
  HotelsAvailabilitySelectors,
} from "@b2bportal/core-hotels";
import type { AvailabilityPageProps } from "@b2bportal/core-hotels";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import {
  HotelListLoading,
  HotelListError,
  HotelList,
  Filters,
  HotelAvailabilityMap,
  AvailabilityMapDialog,
} from "@tenant/hotels";
import { Icon, Page } from "@tenant/ui";
import clsx from "clsx";
import { HotelListEndOfResults } from "../HotelListEndOfResults";
import defaultStyles from "./AvailabilityPage.module.scss";
import { DatePickerProvider } from "./context/DatePickerContext";
import { useRefreshPoints } from "@/components/AuthModule/hooks";
import { SignInBanner } from "@/components/SignInBanner";

const COMPONENT_KEY = CoreHotelsComponent.AvailabilityPage;

export const AvailabilityPage = ({
  context,
  handlers,
  children,
  className,
  ...pageProps
}: AvailabilityPageProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const HotelSlot = useHotelSlot();

  useRefreshPoints();

  const nextPageToken = useSelector(
    HotelsAvailabilitySelectors.getNextPageToken
  );
  const hasMoreResults = nextPageToken != null;
  const showEndOfResults =
    !context.isLoading &&
    !context.isLoadingNext &&
    !hasMoreResults &&
    context.hotelsList.length > 0;

  return (
    <DatePickerProvider>
      <Page className={clsx(block, className)} {...pageProps}>
        <SignInBanner />
        <div className={cn("page-container")}>
          <div className={cn("page-content")}>
            <div className={cn("hotel-action-container")}>
              <div className={cn("search")}>
                <HotelSlot.HotelAvailabilitySearch />
              </div>
              <div className={cn("filter")}>
                <Filters />
              </div>
            </div>
            <div className={cn("container")}>
              <div className={cn("main-container")}>
                {context.isReady && (
                  <div className={cn("result-container")}>
                    {context.isError ? (
                      <HotelListError className={cn("error")} />
                    ) : (
                      <div className={cn("list-container")}>
                        <HotelList onHover={handlers.setHoveredId} />
                        {showEndOfResults && <HotelListEndOfResults />}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={cn("map-container")}>
                <HotelAvailabilityMap
                  loading={!context.mapCenter}
                  hoveredId={context.hoveredId}
                  hotels={context.hotelsList}
                  centroid={context.mapCenter}
                  zoom={context.zoom}
                />
              </div>

              <button
                className={cn("shop-mode-button", [
                  context.shopModeConfig.className ?? "",
                ])}
                onClick={() => context.shopModeConfig.onClick()}
              >
                {context.shopModeConfig.text}
                <Icon iconName={context.shopModeConfig.icon} />
              </button>

              <HotelListLoading isLoading={context.isLoading} />

              <AvailabilityMapDialog
                dialogProps={context.mapDialog.props}
                mapProps={{
                  loading: context.isLoading,
                  hoveredId: context.hoveredId,
                  hotels: context.hotelsList,
                  centroid: context.mapCenter,
                  zoom: context.zoom,
                }}
              />

              {children}
            </div>
          </div>
        </div>
      </Page>
    </DatePickerProvider>
  );
};
